import { AnswerSheetSidebarShimmer } from "../quizzes/AnswerSheetShimmer";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HomeDashboardLayout from "../HomeDashboardLayout";
import AnswerTabs from "../quizzes/AnswerTabs";
import { useRapidFireAnswerSheet } from "../../hooks/useRapidFireAnswerSheet";
import { RAPID_FIRE_ROUTES } from "./rapidFireRoutes";
import {
  buildRapidFireBackTarget,
  buildRapidFireFlowState,
  getStoredRapidFireFlowState,
  persistRapidFireFlowState,
} from "./rapidFireFlowState";

import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../questionBank/QuestionBankList.css";
import "../quizzes/AnswerSheets.css";

import HeaderProfileSection from "../HeaderProfileSection";
import MyWalletSidebar from "../MyWalletSidebar";

const RapidFireAnswerSheets = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeAnswerTab, setActiveAnswerTab] = useState("all");

  const flowState = useMemo(
    () =>
      buildRapidFireFlowState(location.state, getStoredRapidFireFlowState()),
    [location.state],
  );

  const selectedBatch = flowState?.selectedBatch || flowState?.batchData || {};

  const batchId = Number(
    selectedBatch?.batchId || selectedBatch?.batch_id || selectedBatch?.id || 0,
  );

  const statesId = Number(
    flowState?.selectedState?.state_id || flowState?.state_id || 13,
  );

  const categoryId = Number(
    flowState?.selectedCategory?.category_id || flowState?.category_id || 5,
  );

  const subjectId = Number(
    flowState?.selectedSubject?.subject_id ||
      flowState?.selectedSubject?.id ||
      flowState?.subject_id ||
      50,
  );

  const leaderboardNavigationState = useMemo(
    () => ({
      ...flowState,
      rapidFireBackTarget: buildRapidFireBackTarget(
        RAPID_FIRE_ROUTES.answerSheet,
        flowState,
      ),
    }),
    [flowState],
  );

  useEffect(() => {
    persistRapidFireFlowState(flowState);
  }, [flowState]);

  const goToStartRapidFire = () => {
    navigate(RAPID_FIRE_ROUTES.start, {
      state: flowState,
      replace: true,
    });
  };

  const answerSheetQuery = useRapidFireAnswerSheet(
    {
      batch_id: batchId,
      states_id: statesId,
      category_id: categoryId,
      subject_id: subjectId,
    },
    {
      enabled: !!batchId,
    },
  );

  const quizStats = useMemo(
    () => [
      {
        value: answerSheetQuery.data?.totalMarks || "0/0",
        label: "Marks",
      },
      {
        value: answerSheetQuery.data?.rank || "-",
        label: "Rank",
        route: RAPID_FIRE_ROUTES.leaderboard,
      },
      {
        value: answerSheetQuery.data?.timeTaken || "00:00:00",
        label: "Time Taken",
      },
    ],
    [answerSheetQuery.data],
  );

  const userName =
    answerSheetQuery.data?.quizDetail?.user_name ||
    flowState?.quiz_submission?.quiz_detail?.user_name ||
    "Student";

  /* QUESTIONS */

  const hasAnswerSheet = Boolean(answerSheetQuery.data) && !answerSheetQuery.isPlaceholderData;
  const isInitialLoading = !hasAnswerSheet && (answerSheetQuery.isLoading || answerSheetQuery.isFetching || answerSheetQuery.isPlaceholderData);
  const answerQuestions = answerSheetQuery.data?.questions || [];

  /* FILTER COUNTS */

  const answerCounts = {
    all: answerQuestions.length,
    correct: answerQuestions.filter((q) => q.status === "correct").length,
    incorrect: answerQuestions.filter((q) => q.status === "incorrect").length,
    unsolved: answerQuestions.filter((q) => q.status === "unsolved").length,
  };

  const answerFilters = [
    {
      id: "all",
      icon: "bi-list-check",
    },
    {
      id: "correct",
      icon: "bi-check-circle",
    },
    {
      id: "incorrect",
      icon: "bi-x-circle",
    },
    {
      id: "unsolved",
      icon: "bi-arrow-repeat",
    },
  ];

  useEffect(() => {
    window.history.pushState(
      { answerSheetGuard: true },
      "",
      window.location.href,
    );

    const handlePopState = () => {
      goToStartRapidFire();
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [flowState]);

  return (
    <>
      <style>{`
        .homeDashboard-wrapper .page-content {
          padding-right: 0;
        }

        .homeDashboard-wrapper .main-content {
          height: 100vh;
          overflow: scroll;
          box-sizing: border-box;
          padding-bottom: 35px;
          min-height: 0;
          overflow-x: hidden !important;
       }
      .quizInstruction-Btn {
       bottom: 0px;
      }
     @media (max-width: 767px){
      .homeDashboard-wrapper .main-content {
         height: calc(100vh - 0px) !important;
         padding-bottom: 0px!important;
      }
      .instruction-main {
        min-height: calc(100vh - 0px);
        height: calc(100vh - 0px);
     }
      }
      `}</style>

      <HomeDashboardLayout showHeader={false}>
        <div className="qbl-page qbankList-page page-padding fixed-page-btn">
          <div className="start-quiz qbl-main instruction-main">
            {/* PAGE HEADER */}

            <div className="hideProfile-page">
              <div className="categories-page-header mb-0">
                <div>
                  <button
                    type="button"
                    className="categories-back-button"
                    onClick={goToStartRapidFire}
                  >
                    <i className="bi bi-chevron-left" />
                  </button>
                </div>

                <div>
                  <h4 className="d-flex categories-page-title">Answer Sheet</h4>
                </div>

                <div>
                  <HeaderProfileSection />
                </div>
              </div>
            </div>

            {/* MAIN LAYOUT */}

            <div className="section-divide answerSheet-layout" aria-busy={isInitialLoading}>
              {/* LEFT SIDEBAR */}

              <div className="answerSheet-sidebar">
                {isInitialLoading ? <AnswerSheetSidebarShimmer actionCount={1} /> : <div className="answerSheet-sidebarTop">
                  <h1 className="keep-title">Keep Trying, {userName}</h1>

                  <div className="keep-label">
                    Keep Practice, Keep Improving
                  </div>

                  {/* STATS */}

                  <div className="quizAnswer-stats">
                    {quizStats.map((stat) => (
                      <div className="answerSheet-statRow" key={stat.label}>
                        <span className="answerSheet-statLabel">
                          {stat.label}
                        </span>

                        {stat.route ? (
                          <button
                            type="button"
                            className="answerSheet-statValue answerSheet-rankLink"
                            onClick={() =>
                              navigate(stat.route, {
                                state: leaderboardNavigationState,
                              })
                            }
                          >
                            {stat.value}
                          </button>
                        ) : (
                          <strong className="answerSheet-statValue">
                            {stat.value}
                          </strong>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* NAVIGATION BUTTONS */}

                  <div className="answerSheet-sidebarButtons">
                    <button
                      type="button"
                      className="answerSheet-blueBtn"
                      onClick={() =>
                        navigate(RAPID_FIRE_ROUTES.leaderboard, {
                          state: leaderboardNavigationState,
                        })
                      }
                    >
                      <i className="bi bi-trophy-fill" />
                      <span>Leaderboard</span>
                    </button>
                  </div>

                  {/* FILTERS */}

                  <div className="answerSheet-filterSection">
                    <h3 className="answerSheet-filterTitle">Filters</h3>

                    <div className="answerSheet-filterGrid">
                      {answerFilters.map((filter) => (
                        <button
                          key={filter.id}
                          type="button"
                          className={`
                            answerSheet-filterBtn
                            answerSheet-filter-${filter.id}
                            ${activeAnswerTab === filter.id ? "active" : ""}
                          `}
                          onClick={() => setActiveAnswerTab(filter.id)}
                          aria-pressed={activeAnswerTab === filter.id}
                        >
                          <i className={`bi ${filter.icon}`} />
                          <span>{answerCounts[filter.id]}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>}

                {/* REFER & EARN BANNER */}

                {/* <div className="answerSheet-referBanner">
                
                </div> */}
              </div>

              {/* RIGHT QUESTION SECTION */}

              <div className="answerSheet-content">
                {answerSheetQuery.isError && !hasAnswerSheet ? (
                  <div className="quiz-empty mt-3">
                    <i className="bi bi-exclamation-circle" />

                    <h3>Unable to load answer sheet</h3>

                    <p>
                      {answerSheetQuery.error?.message || "Please try again."}
                    </p>
                  </div>
                ) : (
                  <AnswerTabs
                    questions={answerQuestions}
                    isLoading={isInitialLoading}
                    activeTab={activeAnswerTab}
                    onTabChange={setActiveAnswerTab}
                    hideTabs={true}
                  />
                )}
              </div>
            </div>
          </div>

          <MyWalletSidebar withTopOffset={false} />
        </div>
      </HomeDashboardLayout>
    </>
  );
};

export default RapidFireAnswerSheets;
